import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity'
import { createReducer, on, Action } from '@ngrx/store'

import * as PresenterWidgetsActions from './presenter-widgets.actions'
import {
  PRESENTER_WIDGETS_FEATURE_KEY,
  PresenterWidgetsState,
  PresenterWidgetsPartialState,
  CountdownWidgetsState,
  ImageWidgetsState,
  TextWidgetsState,
  SmartTextWidgetsState,
  TotalRaisedWidgetsState,
  TeamScoringWidgetsState,
  PowerPledgeWidgetsState,
  NotificationWidgetsState,
  ItemTableWidgetsState,
  RaffleWinnersWidgetsState,
  HonourRollWidgetsState,
  ThermometerWidgetsState,
  RaffleDrawWidgetsState,
  QrCodeWidgetsState,
  VideoWidgetsState,
  CountdownWidgetModel,
  ImageWidgetModel,
  TextWidgetModel,
  SmartTextWidgetModel,
  TotalRaisedWidgetModel,
  TeamScoringWidgetModel,
  PowerPledgeWidgetModel,
  NotificationWidgetModel,
  ItemTableWidgetModel,
  RaffleWinnersWidgetModel,
  HonourRollWidgetModel,
  ThermometerWidgetModel,
  RaffleDrawWidgetModel,
  QrCodeWidgetModel,
  VideoWidgetModel,
  WidgetModel,
  WidgetType,
} from './presenter-widgets.models'

export { PRESENTER_WIDGETS_FEATURE_KEY }
export type { PresenterWidgetsState, PresenterWidgetsPartialState }

// Entity adapters for each widget type
export const countdownWidgetsAdapter: EntityAdapter<CountdownWidgetModel> = createEntityAdapter<CountdownWidgetModel>({
  selectId: (widget) => widget.uuid,
})

export const imageWidgetsAdapter: EntityAdapter<ImageWidgetModel> = createEntityAdapter<ImageWidgetModel>({
  selectId: (widget) => widget.uuid,
})

export const textWidgetsAdapter: EntityAdapter<TextWidgetModel> = createEntityAdapter<TextWidgetModel>({
  selectId: (widget) => widget.uuid,
})

export const smartTextWidgetsAdapter: EntityAdapter<SmartTextWidgetModel> = createEntityAdapter<SmartTextWidgetModel>({
  selectId: (widget) => widget.uuid,
})

export const totalRaisedWidgetsAdapter: EntityAdapter<TotalRaisedWidgetModel> =
  createEntityAdapter<TotalRaisedWidgetModel>({
    selectId: (widget) => widget.uuid,
  })

export const teamScoringWidgetsAdapter: EntityAdapter<TeamScoringWidgetModel> =
  createEntityAdapter<TeamScoringWidgetModel>({
    selectId: (widget) => widget.uuid,
  })

export const powerPledgeWidgetsAdapter: EntityAdapter<PowerPledgeWidgetModel> =
  createEntityAdapter<PowerPledgeWidgetModel>({
    selectId: (widget) => widget.uuid,
  })

export const notificationWidgetsAdapter: EntityAdapter<NotificationWidgetModel> =
  createEntityAdapter<NotificationWidgetModel>({
    selectId: (widget) => widget.uuid,
  })

export const itemTableWidgetsAdapter: EntityAdapter<ItemTableWidgetModel> = createEntityAdapter<ItemTableWidgetModel>({
  selectId: (widget) => widget.uuid,
})

export const raffleWinnersWidgetsAdapter: EntityAdapter<RaffleWinnersWidgetModel> =
  createEntityAdapter<RaffleWinnersWidgetModel>({
    selectId: (widget) => widget.uuid,
  })

export const honourRollWidgetsAdapter: EntityAdapter<HonourRollWidgetModel> =
  createEntityAdapter<HonourRollWidgetModel>({
    selectId: (widget) => widget.uuid,
  })

export const thermometerWidgetsAdapter: EntityAdapter<ThermometerWidgetModel> =
  createEntityAdapter<ThermometerWidgetModel>({
    selectId: (widget) => widget.uuid,
  })

export const raffleDrawWidgetsAdapter: EntityAdapter<RaffleDrawWidgetModel> =
  createEntityAdapter<RaffleDrawWidgetModel>({
    selectId: (widget) => widget.uuid,
  })

export const qrCodeWidgetsAdapter: EntityAdapter<QrCodeWidgetModel> = createEntityAdapter<QrCodeWidgetModel>({
  selectId: (widget) => widget.uuid,
})

export const videoWidgetsAdapter: EntityAdapter<VideoWidgetModel> = createEntityAdapter<VideoWidgetModel>({
  selectId: (widget) => widget.uuid,
})

// Initial states for each widget type
const initialCountdownWidgetsState: CountdownWidgetsState = countdownWidgetsAdapter.getInitialState({
  loaded: false,
  editCreateLoaded: true,
  createLoaded: true,
  optimisticDeleted: {},
})

const initialImageWidgetsState: ImageWidgetsState = imageWidgetsAdapter.getInitialState({
  loaded: false,
  editCreateLoaded: true,
  createLoaded: true,
  optimisticDeleted: {},
})

const initialTextWidgetsState: TextWidgetsState = textWidgetsAdapter.getInitialState({
  loaded: false,
  editCreateLoaded: true,
  createLoaded: true,
  optimisticDeleted: {},
})

const initialSmartTextWidgetsState: SmartTextWidgetsState = smartTextWidgetsAdapter.getInitialState({
  loaded: false,
  editCreateLoaded: true,
  createLoaded: true,
  optimisticDeleted: {},
})

const initialTotalRaisedWidgetsState: TotalRaisedWidgetsState = totalRaisedWidgetsAdapter.getInitialState({
  loaded: false,
  editCreateLoaded: true,
  createLoaded: true,
  optimisticDeleted: {},
})

const initialTeamScoringWidgetsState: TeamScoringWidgetsState = teamScoringWidgetsAdapter.getInitialState({
  loaded: false,
  editCreateLoaded: true,
  createLoaded: true,
  optimisticDeleted: {},
})

const initialPowerPledgeWidgetsState: PowerPledgeWidgetsState = powerPledgeWidgetsAdapter.getInitialState({
  loaded: false,
  editCreateLoaded: true,
  createLoaded: true,
  optimisticDeleted: {},
})

const initialNotificationWidgetsState: NotificationWidgetsState = notificationWidgetsAdapter.getInitialState({
  loaded: false,
  editCreateLoaded: true,
  createLoaded: true,
  optimisticDeleted: {},
})

const initialItemTableWidgetsState: ItemTableWidgetsState = itemTableWidgetsAdapter.getInitialState({
  loaded: false,
  editCreateLoaded: true,
  createLoaded: true,
  optimisticDeleted: {},
})

const initialRaffleWinnersWidgetsState: RaffleWinnersWidgetsState = raffleWinnersWidgetsAdapter.getInitialState({
  loaded: false,
  editCreateLoaded: true,
  createLoaded: true,
  optimisticDeleted: {},
})

const initialHonourRollWidgetsState: HonourRollWidgetsState = honourRollWidgetsAdapter.getInitialState({
  loaded: false,
  editCreateLoaded: true,
  createLoaded: true,
  optimisticDeleted: {},
})

const initialThermometerWidgetsState: ThermometerWidgetsState = thermometerWidgetsAdapter.getInitialState({
  loaded: false,
  editCreateLoaded: true,
  createLoaded: true,
  optimisticDeleted: {},
})

const initialRaffleDrawWidgetsState: RaffleDrawWidgetsState = raffleDrawWidgetsAdapter.getInitialState({
  loaded: false,
  editCreateLoaded: true,
  createLoaded: true,
  optimisticDeleted: {},
})

const initialQrCodeWidgetsState: QrCodeWidgetsState = qrCodeWidgetsAdapter.getInitialState({
  loaded: false,
  editCreateLoaded: true,
  createLoaded: true,
  optimisticDeleted: {},
})

const initialVideoWidgetsState: VideoWidgetsState = videoWidgetsAdapter.getInitialState({
  loaded: false,
  editCreateLoaded: true,
  createLoaded: true,
  optimisticDeleted: {},
})

// Combined initial state
export const initialPresenterWidgetsState: PresenterWidgetsState = {
  countdownWidgets: initialCountdownWidgetsState,
  imageWidgets: initialImageWidgetsState,
  textWidgets: initialTextWidgetsState,
  smartTextWidgets: initialSmartTextWidgetsState,
  totalRaisedWidgets: initialTotalRaisedWidgetsState,
  teamScoringWidgets: initialTeamScoringWidgetsState,
  powerPledgeWidgets: initialPowerPledgeWidgetsState,
  notificationWidgets: initialNotificationWidgetsState,
  itemTableWidgets: initialItemTableWidgetsState,
  raffleWinnersWidgets: initialRaffleWinnersWidgetsState,
  honourRollWidgets: initialHonourRollWidgetsState,
  thermometerWidgets: initialThermometerWidgetsState,
  raffleDrawWidgets: initialRaffleDrawWidgetsState,
  qrCodeWidgets: initialQrCodeWidgetsState,
  videoWidgets: initialVideoWidgetsState,
  selectedWidget: null,
  selectedWidgetType: null,
  savingWidget: null,
}

// Reducer function
const reducer = createReducer(
  initialPresenterWidgetsState,

  // General actions
  on(PresenterWidgetsActions.clearAllWidgets, () => initialPresenterWidgetsState),

  // Selected widget actions
  on(PresenterWidgetsActions.selectWidget, (state, { widget, widgetType }) => ({
    ...state,
    selectedWidget: widget,
    selectedWidgetType: widgetType,
  })),

  on(PresenterWidgetsActions.clearSelectedWidget, (state) => ({
    ...state,
    selectedWidget: null,
    selectedWidgetType: null,
  })),

  on(PresenterWidgetsActions.replaceWidgetAfterUndoRedo, (state, { widgetType, oldUuid, widget }) => {
    const nextSelectedWidget = state.selectedWidget?.uuid === oldUuid ? (widget as WidgetModel) : state.selectedWidget

    switch (widgetType) {
      case WidgetType.COUNTDOWN:
        return {
          ...state,
          selectedWidget: nextSelectedWidget,
          countdownWidgets: countdownWidgetsAdapter.addOne(
            widget as CountdownWidgetModel,
            countdownWidgetsAdapter.removeOne(oldUuid, state.countdownWidgets),
          ),
        }
      case WidgetType.IMAGE:
        return {
          ...state,
          selectedWidget: nextSelectedWidget,
          imageWidgets: imageWidgetsAdapter.addOne(
            widget as ImageWidgetModel,
            imageWidgetsAdapter.removeOne(oldUuid, state.imageWidgets),
          ),
        }
      case WidgetType.TEXT:
        return {
          ...state,
          selectedWidget: nextSelectedWidget,
          textWidgets: textWidgetsAdapter.addOne(
            widget as TextWidgetModel,
            textWidgetsAdapter.removeOne(oldUuid, state.textWidgets),
          ),
        }
      case WidgetType.SMART_TEXT:
        return {
          ...state,
          selectedWidget: nextSelectedWidget,
          smartTextWidgets: smartTextWidgetsAdapter.addOne(
            widget as SmartTextWidgetModel,
            smartTextWidgetsAdapter.removeOne(oldUuid, state.smartTextWidgets),
          ),
        }
      case WidgetType.TOTAL_RAISED:
        return {
          ...state,
          selectedWidget: nextSelectedWidget,
          totalRaisedWidgets: totalRaisedWidgetsAdapter.addOne(
            widget as TotalRaisedWidgetModel,
            totalRaisedWidgetsAdapter.removeOne(oldUuid, state.totalRaisedWidgets),
          ),
        }
      case WidgetType.TEAM_SCORING:
        return {
          ...state,
          selectedWidget: nextSelectedWidget,
          teamScoringWidgets: teamScoringWidgetsAdapter.addOne(
            widget as TeamScoringWidgetModel,
            teamScoringWidgetsAdapter.removeOne(oldUuid, state.teamScoringWidgets),
          ),
        }
      case WidgetType.POWER_PLEDGE:
        return {
          ...state,
          selectedWidget: nextSelectedWidget,
          powerPledgeWidgets: powerPledgeWidgetsAdapter.addOne(
            widget as PowerPledgeWidgetModel,
            powerPledgeWidgetsAdapter.removeOne(oldUuid, state.powerPledgeWidgets),
          ),
        }
      case WidgetType.NOTIFICATION:
        return {
          ...state,
          selectedWidget: nextSelectedWidget,
          notificationWidgets: notificationWidgetsAdapter.addOne(
            widget as NotificationWidgetModel,
            notificationWidgetsAdapter.removeOne(oldUuid, state.notificationWidgets),
          ),
        }
      case WidgetType.ITEM_TABLE:
        return {
          ...state,
          selectedWidget: nextSelectedWidget,
          itemTableWidgets: itemTableWidgetsAdapter.addOne(
            widget as ItemTableWidgetModel,
            itemTableWidgetsAdapter.removeOne(oldUuid, state.itemTableWidgets),
          ),
        }
      case WidgetType.RAFFLE_WINNERS:
        return {
          ...state,
          selectedWidget: nextSelectedWidget,
          raffleWinnersWidgets: raffleWinnersWidgetsAdapter.addOne(
            widget as RaffleWinnersWidgetModel,
            raffleWinnersWidgetsAdapter.removeOne(oldUuid, state.raffleWinnersWidgets),
          ),
        }
      case WidgetType.HONOUR_ROLL:
        return {
          ...state,
          selectedWidget: nextSelectedWidget,
          honourRollWidgets: honourRollWidgetsAdapter.addOne(
            widget as HonourRollWidgetModel,
            honourRollWidgetsAdapter.removeOne(oldUuid, state.honourRollWidgets),
          ),
        }
      case WidgetType.THERMOMETER:
        return {
          ...state,
          selectedWidget: nextSelectedWidget,
          thermometerWidgets: thermometerWidgetsAdapter.addOne(
            widget as ThermometerWidgetModel,
            thermometerWidgetsAdapter.removeOne(oldUuid, state.thermometerWidgets),
          ),
        }
      case WidgetType.RAFFLE_DRAW:
        return {
          ...state,
          selectedWidget: nextSelectedWidget,
          raffleDrawWidgets: raffleDrawWidgetsAdapter.addOne(
            widget as RaffleDrawWidgetModel,
            raffleDrawWidgetsAdapter.removeOne(oldUuid, state.raffleDrawWidgets),
          ),
        }
      case WidgetType.QR_CODE:
        return {
          ...state,
          selectedWidget: nextSelectedWidget,
          qrCodeWidgets: qrCodeWidgetsAdapter.addOne(
            widget as QrCodeWidgetModel,
            qrCodeWidgetsAdapter.removeOne(oldUuid, state.qrCodeWidgets),
          ),
        }
      default:
        return {
          ...state,
          selectedWidget: nextSelectedWidget,
        }
    }
  }),

  on(PresenterWidgetsActions.updateSelectedWidget, (state, { widget }) => {
    if (!widget?.uuid || !state.selectedWidgetType) {
      return state
    }

    const mergedSelectedWidget =
      state.selectedWidget?.uuid === widget.uuid
        ? ({ ...state.selectedWidget, ...widget } as WidgetModel)
        : state.selectedWidget

    switch (state.selectedWidgetType) {
      case WidgetType.COUNTDOWN:
        return {
          ...state,
          selectedWidget: mergedSelectedWidget,
          countdownWidgets: countdownWidgetsAdapter.updateOne(
            {
              id: widget.uuid,
              changes: widget as Partial<CountdownWidgetModel>,
            },
            state.countdownWidgets,
          ),
        }
      case WidgetType.IMAGE:
        return {
          ...state,
          selectedWidget: mergedSelectedWidget,
          imageWidgets: imageWidgetsAdapter.updateOne(
            {
              id: widget.uuid,
              changes: widget as Partial<ImageWidgetModel>,
            },
            state.imageWidgets,
          ),
        }
      case WidgetType.TEXT:
        return {
          ...state,
          selectedWidget: mergedSelectedWidget,
          textWidgets: textWidgetsAdapter.updateOne(
            {
              id: widget.uuid,
              changes: widget as Partial<TextWidgetModel>,
            },
            state.textWidgets,
          ),
        }
      case WidgetType.SMART_TEXT:
        return {
          ...state,
          selectedWidget: mergedSelectedWidget,
          smartTextWidgets: smartTextWidgetsAdapter.updateOne(
            {
              id: widget.uuid,
              changes: widget as Partial<SmartTextWidgetModel>,
            },
            state.smartTextWidgets,
          ),
        }
      case WidgetType.TOTAL_RAISED:
        return {
          ...state,
          selectedWidget: mergedSelectedWidget,
          totalRaisedWidgets: totalRaisedWidgetsAdapter.updateOne(
            {
              id: widget.uuid,
              changes: widget as Partial<TotalRaisedWidgetModel>,
            },
            state.totalRaisedWidgets,
          ),
        }
      case WidgetType.TEAM_SCORING:
        return {
          ...state,
          selectedWidget: mergedSelectedWidget,
          teamScoringWidgets: teamScoringWidgetsAdapter.updateOne(
            {
              id: widget.uuid,
              changes: widget as Partial<TeamScoringWidgetModel>,
            },
            state.teamScoringWidgets,
          ),
        }
      case WidgetType.POWER_PLEDGE:
        return {
          ...state,
          selectedWidget: mergedSelectedWidget,
          powerPledgeWidgets: powerPledgeWidgetsAdapter.updateOne(
            {
              id: widget.uuid,
              changes: widget as Partial<PowerPledgeWidgetModel>,
            },
            state.powerPledgeWidgets,
          ),
        }
      case WidgetType.NOTIFICATION:
        return {
          ...state,
          selectedWidget: mergedSelectedWidget,
          notificationWidgets: notificationWidgetsAdapter.updateOne(
            {
              id: widget.uuid,
              changes: widget as Partial<NotificationWidgetModel>,
            },
            state.notificationWidgets,
          ),
        }
      case WidgetType.ITEM_TABLE:
        return {
          ...state,
          selectedWidget: mergedSelectedWidget,
          itemTableWidgets: itemTableWidgetsAdapter.updateOne(
            {
              id: widget.uuid,
              changes: widget as Partial<ItemTableWidgetModel>,
            },
            state.itemTableWidgets,
          ),
        }
      case WidgetType.RAFFLE_WINNERS:
        return {
          ...state,
          selectedWidget: mergedSelectedWidget,
          raffleWinnersWidgets: raffleWinnersWidgetsAdapter.updateOne(
            {
              id: widget.uuid,
              changes: widget as Partial<RaffleWinnersWidgetModel>,
            },
            state.raffleWinnersWidgets,
          ),
        }
      case WidgetType.HONOUR_ROLL:
        return {
          ...state,
          selectedWidget: mergedSelectedWidget,
          honourRollWidgets: honourRollWidgetsAdapter.updateOne(
            {
              id: widget.uuid,
              changes: widget as Partial<HonourRollWidgetModel>,
            },
            state.honourRollWidgets,
          ),
        }
      case WidgetType.THERMOMETER:
        return {
          ...state,
          selectedWidget: mergedSelectedWidget,
          thermometerWidgets: thermometerWidgetsAdapter.updateOne(
            {
              id: widget.uuid,
              changes: widget as Partial<ThermometerWidgetModel>,
            },
            state.thermometerWidgets,
          ),
        }
      case WidgetType.RAFFLE_DRAW:
        return {
          ...state,
          selectedWidget: mergedSelectedWidget,
          raffleDrawWidgets: raffleDrawWidgetsAdapter.updateOne(
            {
              id: widget.uuid,
              changes: widget as Partial<RaffleDrawWidgetModel>,
            },
            state.raffleDrawWidgets,
          ),
        }
      case WidgetType.QR_CODE:
        return {
          ...state,
          selectedWidget: mergedSelectedWidget,
          qrCodeWidgets: qrCodeWidgetsAdapter.updateOne(
            {
              id: widget.uuid,
              changes: widget as Partial<QrCodeWidgetModel>,
            },
            state.qrCodeWidgets,
          ),
        }
      default:
        return {
          ...state,
          selectedWidget: mergedSelectedWidget,
        }
    }
  }),

  on(PresenterWidgetsActions.updateSelectedWidgetLocal, (state, { widget }) => {
    if (!widget?.uuid || !state.selectedWidgetType) {
      return state
    }

    const mergedSelectedWidget =
      state.selectedWidget?.uuid === widget.uuid
        ? ({ ...state.selectedWidget, ...widget } as WidgetModel)
        : state.selectedWidget

    switch (state.selectedWidgetType) {
      case WidgetType.COUNTDOWN:
        return {
          ...state,
          selectedWidget: mergedSelectedWidget,
          countdownWidgets: countdownWidgetsAdapter.updateOne(
            {
              id: widget.uuid,
              changes: widget as Partial<CountdownWidgetModel>,
            },
            state.countdownWidgets,
          ),
        }
      case WidgetType.IMAGE:
        return {
          ...state,
          selectedWidget: mergedSelectedWidget,
          imageWidgets: imageWidgetsAdapter.updateOne(
            {
              id: widget.uuid,
              changes: widget as Partial<ImageWidgetModel>,
            },
            state.imageWidgets,
          ),
        }
      case WidgetType.TEXT:
        return {
          ...state,
          selectedWidget: mergedSelectedWidget,
          textWidgets: textWidgetsAdapter.updateOne(
            {
              id: widget.uuid,
              changes: widget as Partial<TextWidgetModel>,
            },
            state.textWidgets,
          ),
        }
      case WidgetType.SMART_TEXT:
        return {
          ...state,
          selectedWidget: mergedSelectedWidget,
          smartTextWidgets: smartTextWidgetsAdapter.updateOne(
            {
              id: widget.uuid,
              changes: widget as Partial<SmartTextWidgetModel>,
            },
            state.smartTextWidgets,
          ),
        }
      case WidgetType.TOTAL_RAISED:
        return {
          ...state,
          selectedWidget: mergedSelectedWidget,
          totalRaisedWidgets: totalRaisedWidgetsAdapter.updateOne(
            {
              id: widget.uuid,
              changes: widget as Partial<TotalRaisedWidgetModel>,
            },
            state.totalRaisedWidgets,
          ),
        }
      case WidgetType.TEAM_SCORING:
        return {
          ...state,
          selectedWidget: mergedSelectedWidget,
          teamScoringWidgets: teamScoringWidgetsAdapter.updateOne(
            {
              id: widget.uuid,
              changes: widget as Partial<TeamScoringWidgetModel>,
            },
            state.teamScoringWidgets,
          ),
        }
      case WidgetType.POWER_PLEDGE:
        return {
          ...state,
          selectedWidget: mergedSelectedWidget,
          powerPledgeWidgets: powerPledgeWidgetsAdapter.updateOne(
            {
              id: widget.uuid,
              changes: widget as Partial<PowerPledgeWidgetModel>,
            },
            state.powerPledgeWidgets,
          ),
        }
      case WidgetType.NOTIFICATION:
        return {
          ...state,
          selectedWidget: mergedSelectedWidget,
          notificationWidgets: notificationWidgetsAdapter.updateOne(
            {
              id: widget.uuid,
              changes: widget as Partial<NotificationWidgetModel>,
            },
            state.notificationWidgets,
          ),
        }
      case WidgetType.ITEM_TABLE:
        return {
          ...state,
          selectedWidget: mergedSelectedWidget,
          itemTableWidgets: itemTableWidgetsAdapter.updateOne(
            {
              id: widget.uuid,
              changes: widget as Partial<ItemTableWidgetModel>,
            },
            state.itemTableWidgets,
          ),
        }
      case WidgetType.RAFFLE_WINNERS:
        return {
          ...state,
          selectedWidget: mergedSelectedWidget,
          raffleWinnersWidgets: raffleWinnersWidgetsAdapter.updateOne(
            {
              id: widget.uuid,
              changes: widget as Partial<RaffleWinnersWidgetModel>,
            },
            state.raffleWinnersWidgets,
          ),
        }
      case WidgetType.HONOUR_ROLL:
        return {
          ...state,
          selectedWidget: mergedSelectedWidget,
          honourRollWidgets: honourRollWidgetsAdapter.updateOne(
            {
              id: widget.uuid,
              changes: widget as Partial<HonourRollWidgetModel>,
            },
            state.honourRollWidgets,
          ),
        }
      case WidgetType.THERMOMETER:
        return {
          ...state,
          selectedWidget: mergedSelectedWidget,
          thermometerWidgets: thermometerWidgetsAdapter.updateOne(
            {
              id: widget.uuid,
              changes: widget as Partial<ThermometerWidgetModel>,
            },
            state.thermometerWidgets,
          ),
        }
      case WidgetType.RAFFLE_DRAW:
        return {
          ...state,
          selectedWidget: mergedSelectedWidget,
          raffleDrawWidgets: raffleDrawWidgetsAdapter.updateOne(
            {
              id: widget.uuid,
              changes: widget as Partial<RaffleDrawWidgetModel>,
            },
            state.raffleDrawWidgets,
          ),
        }
      case WidgetType.QR_CODE:
        return {
          ...state,
          selectedWidget: mergedSelectedWidget,
          qrCodeWidgets: qrCodeWidgetsAdapter.updateOne(
            {
              id: widget.uuid,
              changes: widget as Partial<QrCodeWidgetModel>,
            },
            state.qrCodeWidgets,
          ),
        }
      default:
        return {
          ...state,
          selectedWidget: mergedSelectedWidget,
        }
    }
  }),

  on(PresenterWidgetsActions.reorderWidgets, (state, { updates }) => {
    const apply = <T extends { uuid: string; z?: number }, S extends EntityState<T>>(
      adapter: EntityAdapter<T>,
      slice: S,
      widgetType: WidgetType,
    ): S => {
      const typeUpdates = updates
        .filter((u) => u.widgetType === widgetType)
        .map((u) => ({ id: u.uuid, changes: { z: u.z } as Partial<T> }))

      if (!typeUpdates.length) {
        return slice
      }

      return adapter.updateMany(typeUpdates, slice) as unknown as S
    }

    return {
      ...state,
      countdownWidgets: apply(countdownWidgetsAdapter, state.countdownWidgets, WidgetType.COUNTDOWN),
      imageWidgets: apply(imageWidgetsAdapter, state.imageWidgets, WidgetType.IMAGE),
      textWidgets: apply(textWidgetsAdapter, state.textWidgets, WidgetType.TEXT),
      smartTextWidgets: apply(smartTextWidgetsAdapter, state.smartTextWidgets, WidgetType.SMART_TEXT),
      totalRaisedWidgets: apply(totalRaisedWidgetsAdapter, state.totalRaisedWidgets, WidgetType.TOTAL_RAISED),
      teamScoringWidgets: apply(teamScoringWidgetsAdapter, state.teamScoringWidgets, WidgetType.TEAM_SCORING),
      powerPledgeWidgets: apply(powerPledgeWidgetsAdapter, state.powerPledgeWidgets, WidgetType.POWER_PLEDGE),
      notificationWidgets: apply(notificationWidgetsAdapter, state.notificationWidgets, WidgetType.NOTIFICATION),
      itemTableWidgets: apply(itemTableWidgetsAdapter, state.itemTableWidgets, WidgetType.ITEM_TABLE),
      raffleWinnersWidgets: apply(raffleWinnersWidgetsAdapter, state.raffleWinnersWidgets, WidgetType.RAFFLE_WINNERS),
      honourRollWidgets: apply(honourRollWidgetsAdapter, state.honourRollWidgets, WidgetType.HONOUR_ROLL),
      thermometerWidgets: apply(thermometerWidgetsAdapter, state.thermometerWidgets, WidgetType.THERMOMETER),
      raffleDrawWidgets: apply(raffleDrawWidgetsAdapter, state.raffleDrawWidgets, WidgetType.RAFFLE_DRAW),
      qrCodeWidgets: apply(qrCodeWidgetsAdapter, state.qrCodeWidgets, WidgetType.QR_CODE),
      videoWidgets: apply(videoWidgetsAdapter, state.videoWidgets, WidgetType.VIDEO),
      selectedWidget: state.selectedWidget
        ? {
            ...state.selectedWidget,
            z: updates.find((u) => u.uuid === state.selectedWidget?.uuid)?.z ?? state.selectedWidget.z,
          }
        : null,
    }
  }),

  on(PresenterWidgetsActions.reorderWidgetsSuccess, (state) => state),

  on(PresenterWidgetsActions.updateSelectedTextWidget, (state, { widget }) => ({
    ...state,
    savingWidget: widget,
  })),
  on(PresenterWidgetsActions.updateSelectedSmartTextWidget, (state, { widget }) => ({
    ...state,
    savingWidget: widget,
  })),
  on(PresenterWidgetsActions.updateSelectedItemTableWidget, (state, { widget }) => ({
    ...state,
    savingWidget: widget,
  })),
  on(PresenterWidgetsActions.updateSelectedImageWidget, (state, { widget }) => ({
    ...state,
    savingWidget: widget,
  })),
  on(PresenterWidgetsActions.updateSelectedCountdownWidget, (state, { widget }) => ({
    ...state,
    savingWidget: widget,
  })),
  on(PresenterWidgetsActions.updateSelectedTotalRaisedWidget, (state, { widget }) => ({
    ...state,
    savingWidget: widget,
  })),
  on(PresenterWidgetsActions.updateSelectedTeamScoringWidget, (state, { widget }) => ({
    ...state,
    savingWidget: widget,
  })),
  on(PresenterWidgetsActions.updateSelectedNotificationWidget, (state, { widget }) => ({
    ...state,
    savingWidget: widget,
  })),
  on(PresenterWidgetsActions.updateSelectedRaffleWinnersWidget, (state, { widget }) => ({
    ...state,
    savingWidget: widget,
  })),
  on(PresenterWidgetsActions.updateSelectedHonourRollWidget, (state, { widget }) => ({
    ...state,
    savingWidget: widget,
  })),
  on(PresenterWidgetsActions.updateSelectedThermometerWidget, (state, { widget }) => ({
    ...state,
    savingWidget: widget,
  })),
  on(PresenterWidgetsActions.updateSelectedRaffleDrawWidget, (state, { widget }) => ({
    ...state,
    savingWidget: widget,
  })),
  on(PresenterWidgetsActions.updateSelectedQrCodeWidget, (state, { widget }) => ({
    ...state,
    savingWidget: widget,
  })),
  on(
    PresenterWidgetsActions.updateSelectedTextWidgetSuccess,
    PresenterWidgetsActions.updateSelectedTextWidgetFailure,
    PresenterWidgetsActions.updateSelectedSmartTextWidgetSuccess,
    PresenterWidgetsActions.updateSelectedSmartTextWidgetFailure,
    PresenterWidgetsActions.updateSelectedItemTableWidgetSuccess,
    PresenterWidgetsActions.updateSelectedItemTableWidgetFailure,
    PresenterWidgetsActions.updateSelectedImageWidgetFailure,
    PresenterWidgetsActions.updateSelectedImageWidgetSuccess,
    PresenterWidgetsActions.updateSelectedCountdownWidgetFailure,
    PresenterWidgetsActions.updateSelectedCountdownWidgetSuccess,
    PresenterWidgetsActions.updateSelectedTotalRaisedWidgetFailure,
    PresenterWidgetsActions.updateSelectedTotalRaisedWidgetSuccess,
    PresenterWidgetsActions.updateSelectedTeamScoringWidgetFailure,
    PresenterWidgetsActions.updateSelectedTeamScoringWidgetSuccess,
    PresenterWidgetsActions.updateSelectedNotificationWidgetFailure,
    PresenterWidgetsActions.updateSelectedNotificationWidgetSuccess,
    PresenterWidgetsActions.updateSelectedRaffleWinnersWidgetFailure,
    PresenterWidgetsActions.updateSelectedRaffleWinnersWidgetSuccess,
    PresenterWidgetsActions.updateSelectedHonourRollWidgetFailure,
    PresenterWidgetsActions.updateSelectedHonourRollWidgetSuccess,
    PresenterWidgetsActions.updateSelectedThermometerWidgetFailure,
    PresenterWidgetsActions.updateSelectedThermometerWidgetSuccess,
    PresenterWidgetsActions.updateSelectedRaffleDrawWidgetFailure,
    PresenterWidgetsActions.updateSelectedRaffleDrawWidgetSuccess,
    PresenterWidgetsActions.updateSelectedQrCodeWidgetFailure,
    PresenterWidgetsActions.updateSelectedQrCodeWidgetSuccess,
    (state) => ({
      ...state,
      savingWidget: null,
    }),
  ),

  // Countdown Widget actions
  on(PresenterWidgetsActions.loadCountdownWidgets, (state) => ({
    ...state,
    countdownWidgets: { ...state.countdownWidgets, loaded: false, error: null },
  })),
  on(PresenterWidgetsActions.loadCountdownWidgetsSuccess, (state, { widgets }) => ({
    ...state,
    countdownWidgets: countdownWidgetsAdapter.setAll(widgets, { ...state.countdownWidgets, loaded: true }),
  })),
  on(PresenterWidgetsActions.loadCountdownWidgetsFailure, (state, { error }) => ({
    ...state,
    countdownWidgets: { ...state.countdownWidgets, error },
  })),
  on(PresenterWidgetsActions.selectCountdownWidget, (state, { uuid }) => ({
    ...state,
    countdownWidgets: { ...state.countdownWidgets, selectedId: uuid },
  })),
  on(PresenterWidgetsActions.updateSelectedCountdownWidgetSuccess, (state, { update }) => ({
    ...state,
    countdownWidgets: countdownWidgetsAdapter.updateOne(update, state.countdownWidgets),
  })),
  on(PresenterWidgetsActions.updateSelectedCountdownWidgetFailure, (state, { error }) => ({
    ...state,
    countdownWidgets: { ...state.countdownWidgets, error },
  })),
  on(PresenterWidgetsActions.clearSelectedCountdownWidget, (state) => ({
    ...state,
    countdownWidgets: { ...state.countdownWidgets, selectedId: undefined },
  })),
  on(PresenterWidgetsActions.createCountdownWidget, (state) => ({
    ...state,
    countdownWidgets: { ...state.countdownWidgets, createLoaded: false },
  })),
  on(PresenterWidgetsActions.createCountdownWidgetSuccess, (state, { widget }) => ({
    ...state,
    countdownWidgets: countdownWidgetsAdapter.addOne(widget, { ...state.countdownWidgets, createLoaded: true }),
  })),
  on(PresenterWidgetsActions.createCountdownWidgetFailure, (state, { error }) => ({
    ...state,
    countdownWidgets: { ...state.countdownWidgets, error, createLoaded: true },
  })),
  on(PresenterWidgetsActions.deleteCountdownWidget, (state, { uuid }) => {
    const deleted = state.countdownWidgets.entities[uuid]
    const nextState = countdownWidgetsAdapter.removeOne(uuid, state.countdownWidgets)
    return {
      ...state,
      countdownWidgets: {
        ...nextState,
        optimisticDeleted: deleted
          ? { ...state.countdownWidgets.optimisticDeleted, [uuid]: deleted }
          : state.countdownWidgets.optimisticDeleted,
      },
    }
  }),
  on(PresenterWidgetsActions.deleteCountdownWidgetSuccess, (state, { uuid }) => ({
    ...state,
    countdownWidgets: {
      ...state.countdownWidgets,
      optimisticDeleted: Object.fromEntries(
        Object.entries(state.countdownWidgets.optimisticDeleted).filter(([key]) => key !== uuid),
      ),
    },
  })),
  on(PresenterWidgetsActions.deleteCountdownWidgetFailure, (state, { error }) => ({
    ...state,
    countdownWidgets: {
      ...state.countdownWidgets,
      error,
      optimisticDeleted: {},
      ...(Object.keys(state.countdownWidgets.optimisticDeleted).length
        ? countdownWidgetsAdapter.addMany(
            Object.values(state.countdownWidgets.optimisticDeleted),
            state.countdownWidgets,
          )
        : {}),
    },
  })),

  // Image Widget actions
  on(PresenterWidgetsActions.loadImageWidgets, (state) => ({
    ...state,
    imageWidgets: { ...state.imageWidgets, loaded: false, error: null },
  })),
  on(PresenterWidgetsActions.loadImageWidgetsSuccess, (state, { widgets }) => ({
    ...state,
    imageWidgets: imageWidgetsAdapter.setAll(widgets, { ...state.imageWidgets, loaded: true }),
  })),
  on(PresenterWidgetsActions.loadImageWidgetsFailure, (state, { error }) => ({
    ...state,
    imageWidgets: { ...state.imageWidgets, error },
  })),
  on(PresenterWidgetsActions.selectImageWidget, (state, { uuid }) => ({
    ...state,
    imageWidgets: { ...state.imageWidgets, selectedId: uuid },
  })),
  on(PresenterWidgetsActions.updateSelectedImageWidgetSuccess, (state, { update }) => ({
    ...state,
    imageWidgets: imageWidgetsAdapter.updateOne(update, state.imageWidgets),
  })),
  on(PresenterWidgetsActions.updateSelectedImageWidgetFailure, (state, { error }) => ({
    ...state,
    imageWidgets: { ...state.imageWidgets, error },
  })),
  on(PresenterWidgetsActions.clearSelectedImageWidget, (state) => ({
    ...state,
    imageWidgets: { ...state.imageWidgets, selectedId: undefined },
  })),
  on(PresenterWidgetsActions.createImageWidget, (state) => ({
    ...state,
    imageWidgets: { ...state.imageWidgets, createLoaded: false },
  })),
  on(PresenterWidgetsActions.createImageWidgetSuccess, (state, { widget }) => ({
    ...state,
    imageWidgets: imageWidgetsAdapter.addOne(widget, { ...state.imageWidgets, createLoaded: true }),
  })),
  on(PresenterWidgetsActions.createImageWidgetFailure, (state, { error }) => ({
    ...state,
    imageWidgets: { ...state.imageWidgets, error, createLoaded: true },
  })),
  on(PresenterWidgetsActions.deleteImageWidget, (state, { uuid }) => {
    const deleted = state.imageWidgets.entities[uuid]
    const nextState = imageWidgetsAdapter.removeOne(uuid, state.imageWidgets)
    return {
      ...state,
      imageWidgets: {
        ...nextState,
        optimisticDeleted: deleted
          ? { ...state.imageWidgets.optimisticDeleted, [uuid]: deleted }
          : state.imageWidgets.optimisticDeleted,
      },
    }
  }),
  on(PresenterWidgetsActions.deleteImageWidgetSuccess, (state, { uuid }) => ({
    ...state,
    imageWidgets: {
      ...state.imageWidgets,
      optimisticDeleted: Object.fromEntries(
        Object.entries(state.imageWidgets.optimisticDeleted).filter(([key]) => key !== uuid),
      ),
    },
  })),
  on(PresenterWidgetsActions.deleteImageWidgetFailure, (state, { error }) => ({
    ...state,
    imageWidgets: {
      ...state.imageWidgets,
      error,
      optimisticDeleted: {},
      ...(Object.keys(state.imageWidgets.optimisticDeleted).length
        ? imageWidgetsAdapter.addMany(Object.values(state.imageWidgets.optimisticDeleted), state.imageWidgets)
        : {}),
    },
  })),

  // Text Widget actions
  on(PresenterWidgetsActions.loadTextWidgets, (state) => ({
    ...state,
    textWidgets: { ...state.textWidgets, loaded: false, error: null },
  })),
  on(PresenterWidgetsActions.loadTextWidgetsSuccess, (state, { widgets }) => ({
    ...state,
    textWidgets: textWidgetsAdapter.setAll(widgets, { ...state.textWidgets, loaded: true }),
  })),
  on(PresenterWidgetsActions.loadTextWidgetsFailure, (state, { error }) => ({
    ...state,
    textWidgets: { ...state.textWidgets, error },
  })),
  on(PresenterWidgetsActions.selectTextWidget, (state, { uuid }) => ({
    ...state,
    textWidgets: { ...state.textWidgets, selectedId: uuid },
  })),
  on(PresenterWidgetsActions.updateSelectedTextWidget, (state) => ({
    ...state,
    textWidgets: { ...state.textWidgets, editCreateLoaded: false },
  })),
  on(
    PresenterWidgetsActions.updateSelectedTextWidgetSuccess,
    PresenterWidgetsActions.updateSelectedTextWidgetFailure,
    (state) => ({
      ...state,
      textWidgets: { ...state.textWidgets, editCreateLoaded: true },
    }),
  ),
  on(PresenterWidgetsActions.updateSelectedTextWidgetSuccess, (state, { update }) => ({
    ...state,
    textWidgets: textWidgetsAdapter.updateOne(update, state.textWidgets),
  })),
  on(PresenterWidgetsActions.updateSelectedTextWidgetFailure, (state, { error }) => ({
    ...state,
    textWidgets: { ...state.textWidgets, error },
  })),
  on(PresenterWidgetsActions.clearSelectedTextWidget, (state) => ({
    ...state,
    textWidgets: { ...state.textWidgets, selectedId: undefined },
  })),
  on(PresenterWidgetsActions.createTextWidget, (state) => ({
    ...state,
    textWidgets: { ...state.textWidgets, createLoaded: false },
  })),
  on(PresenterWidgetsActions.createTextWidgetSuccess, (state, { widget }) => ({
    ...state,
    textWidgets: textWidgetsAdapter.addOne(widget, { ...state.textWidgets, createLoaded: true }),
  })),
  on(PresenterWidgetsActions.createTextWidgetFailure, (state, { error }) => ({
    ...state,
    textWidgets: { ...state.textWidgets, error, createLoaded: true },
  })),
  on(PresenterWidgetsActions.deleteTextWidget, (state, { uuid }) => {
    const deleted = state.textWidgets.entities[uuid]
    const nextState = textWidgetsAdapter.removeOne(uuid, state.textWidgets)
    return {
      ...state,
      textWidgets: {
        ...nextState,
        optimisticDeleted: deleted
          ? { ...state.textWidgets.optimisticDeleted, [uuid]: deleted }
          : state.textWidgets.optimisticDeleted,
      },
    }
  }),
  on(PresenterWidgetsActions.deleteTextWidgetSuccess, (state, { uuid }) => ({
    ...state,
    textWidgets: {
      ...state.textWidgets,
      optimisticDeleted: Object.fromEntries(
        Object.entries(state.textWidgets.optimisticDeleted).filter(([key]) => key !== uuid),
      ),
    },
  })),
  on(PresenterWidgetsActions.deleteTextWidgetFailure, (state, { error }) => ({
    ...state,
    textWidgets: {
      ...state.textWidgets,
      error,
      optimisticDeleted: {},
      ...(Object.keys(state.textWidgets.optimisticDeleted).length
        ? textWidgetsAdapter.addMany(Object.values(state.textWidgets.optimisticDeleted), state.textWidgets)
        : {}),
    },
  })),

  // Smart Text Widget actions
  on(PresenterWidgetsActions.loadSmartTextWidgets, (state) => ({
    ...state,
    smartTextWidgets: { ...state.smartTextWidgets, loaded: false, error: null },
  })),
  on(PresenterWidgetsActions.loadSmartTextWidgetsSuccess, (state, { widgets }) => ({
    ...state,
    smartTextWidgets: smartTextWidgetsAdapter.setAll(widgets, { ...state.smartTextWidgets, loaded: true }),
  })),
  on(PresenterWidgetsActions.loadSmartTextWidgetsFailure, (state, { error }) => ({
    ...state,
    smartTextWidgets: { ...state.smartTextWidgets, error },
  })),
  on(PresenterWidgetsActions.selectSmartTextWidget, (state, { uuid }) => ({
    ...state,
    smartTextWidgets: { ...state.smartTextWidgets, selectedId: uuid },
  })),
  on(PresenterWidgetsActions.updateSelectedSmartTextWidget, (state) => ({
    ...state,
    smartTextWidgets: { ...state.smartTextWidgets, editCreateLoaded: false },
  })),
  on(
    PresenterWidgetsActions.updateSelectedSmartTextWidgetSuccess,
    PresenterWidgetsActions.updateSelectedSmartTextWidgetFailure,
    (state) => ({
      ...state,
      smartTextWidgets: { ...state.smartTextWidgets, editCreateLoaded: true },
    }),
  ),
  on(PresenterWidgetsActions.updateSelectedSmartTextWidgetSuccess, (state, { update }) => ({
    ...state,
    smartTextWidgets: smartTextWidgetsAdapter.updateOne(update, state.smartTextWidgets),
  })),
  on(PresenterWidgetsActions.updateSelectedSmartTextWidgetFailure, (state, { error }) => ({
    ...state,
    smartTextWidgets: { ...state.smartTextWidgets, error },
  })),
  on(PresenterWidgetsActions.clearSelectedSmartTextWidget, (state) => ({
    ...state,
    smartTextWidgets: { ...state.smartTextWidgets, selectedId: undefined },
  })),
  on(PresenterWidgetsActions.createSmartTextWidget, (state) => ({
    ...state,
    smartTextWidgets: { ...state.smartTextWidgets, createLoaded: false },
  })),
  on(PresenterWidgetsActions.createSmartTextWidgetSuccess, (state, { widget }) => ({
    ...state,
    smartTextWidgets: smartTextWidgetsAdapter.addOne(widget, { ...state.smartTextWidgets, createLoaded: true }),
  })),
  on(PresenterWidgetsActions.createSmartTextWidgetFailure, (state, { error }) => ({
    ...state,
    smartTextWidgets: { ...state.smartTextWidgets, error, createLoaded: true },
  })),
  on(PresenterWidgetsActions.deleteSmartTextWidget, (state, { uuid }) => {
    const deleted = state.smartTextWidgets.entities[uuid]
    const nextState = smartTextWidgetsAdapter.removeOne(uuid, state.smartTextWidgets)
    return {
      ...state,
      smartTextWidgets: {
        ...nextState,
        optimisticDeleted: deleted
          ? { ...state.smartTextWidgets.optimisticDeleted, [uuid]: deleted }
          : state.smartTextWidgets.optimisticDeleted,
      },
    }
  }),
  on(PresenterWidgetsActions.deleteSmartTextWidgetSuccess, (state, { uuid }) => ({
    ...state,
    smartTextWidgets: {
      ...state.smartTextWidgets,
      optimisticDeleted: Object.fromEntries(
        Object.entries(state.smartTextWidgets.optimisticDeleted).filter(([key]) => key !== uuid),
      ),
    },
  })),
  on(PresenterWidgetsActions.deleteSmartTextWidgetFailure, (state, { error }) => ({
    ...state,
    smartTextWidgets: {
      ...state.smartTextWidgets,
      error,
      optimisticDeleted: {},
      ...(Object.keys(state.smartTextWidgets.optimisticDeleted).length
        ? smartTextWidgetsAdapter.addMany(
            Object.values(state.smartTextWidgets.optimisticDeleted),
            state.smartTextWidgets,
          )
        : {}),
    },
  })),

  // Total Raised Widget actions
  on(PresenterWidgetsActions.loadTotalRaisedWidgets, (state) => ({
    ...state,
    totalRaisedWidgets: { ...state.totalRaisedWidgets, loaded: false, error: null },
  })),
  on(PresenterWidgetsActions.loadTotalRaisedWidgetsSuccess, (state, { widgets }) => ({
    ...state,
    totalRaisedWidgets: totalRaisedWidgetsAdapter.setAll(widgets, {
      ...state.totalRaisedWidgets,
      loaded: true,
    }),
  })),
  on(PresenterWidgetsActions.loadTotalRaisedWidgetsFailure, (state, { error }) => ({
    ...state,
    totalRaisedWidgets: { ...state.totalRaisedWidgets, error },
  })),
  on(PresenterWidgetsActions.selectTotalRaisedWidget, (state, { uuid }) => ({
    ...state,
    totalRaisedWidgets: { ...state.totalRaisedWidgets, selectedId: uuid },
  })),
  on(PresenterWidgetsActions.updateSelectedTotalRaisedWidgetSuccess, (state, { update }) => ({
    ...state,
    totalRaisedWidgets: totalRaisedWidgetsAdapter.updateOne(update, state.totalRaisedWidgets),
  })),
  on(PresenterWidgetsActions.updateSelectedTotalRaisedWidgetFailure, (state, { error }) => ({
    ...state,
    totalRaisedWidgets: { ...state.totalRaisedWidgets, error },
  })),
  on(PresenterWidgetsActions.clearSelectedTotalRaisedWidget, (state) => ({
    ...state,
    totalRaisedWidgets: { ...state.totalRaisedWidgets, selectedId: undefined },
  })),
  on(PresenterWidgetsActions.createTotalRaisedWidget, (state) => ({
    ...state,
    totalRaisedWidgets: { ...state.totalRaisedWidgets, createLoaded: false },
  })),
  on(PresenterWidgetsActions.createTotalRaisedWidgetSuccess, (state, { widget }) => ({
    ...state,
    totalRaisedWidgets: totalRaisedWidgetsAdapter.addOne(widget, { ...state.totalRaisedWidgets, createLoaded: true }),
  })),
  on(PresenterWidgetsActions.createTotalRaisedWidgetFailure, (state, { error }) => ({
    ...state,
    totalRaisedWidgets: { ...state.totalRaisedWidgets, error, createLoaded: true },
  })),
  on(PresenterWidgetsActions.deleteTotalRaisedWidget, (state, { uuid }) => {
    const deleted = state.totalRaisedWidgets.entities[uuid]
    const nextState = totalRaisedWidgetsAdapter.removeOne(uuid, state.totalRaisedWidgets)
    return {
      ...state,
      totalRaisedWidgets: {
        ...nextState,
        optimisticDeleted: deleted
          ? { ...state.totalRaisedWidgets.optimisticDeleted, [uuid]: deleted }
          : state.totalRaisedWidgets.optimisticDeleted,
      },
    }
  }),
  on(PresenterWidgetsActions.deleteTotalRaisedWidgetSuccess, (state, { uuid }) => ({
    ...state,
    totalRaisedWidgets: {
      ...state.totalRaisedWidgets,
      optimisticDeleted: Object.fromEntries(
        Object.entries(state.totalRaisedWidgets.optimisticDeleted).filter(([key]) => key !== uuid),
      ),
    },
  })),
  on(PresenterWidgetsActions.deleteTotalRaisedWidgetFailure, (state, { error }) => ({
    ...state,
    totalRaisedWidgets: {
      ...state.totalRaisedWidgets,
      error,
      optimisticDeleted: {},
      ...(Object.keys(state.totalRaisedWidgets.optimisticDeleted).length
        ? totalRaisedWidgetsAdapter.addMany(
            Object.values(state.totalRaisedWidgets.optimisticDeleted),
            state.totalRaisedWidgets,
          )
        : {}),
    },
  })),

  // Team Scoring Widget actions
  on(PresenterWidgetsActions.loadTeamScoringWidgets, (state) => ({
    ...state,
    teamScoringWidgets: { ...state.teamScoringWidgets, loaded: false, error: null },
  })),
  on(PresenterWidgetsActions.loadTeamScoringWidgetsSuccess, (state, { widgets }) => ({
    ...state,
    teamScoringWidgets: teamScoringWidgetsAdapter.setAll(widgets, {
      ...state.teamScoringWidgets,
      loaded: true,
    }),
  })),
  on(PresenterWidgetsActions.loadTeamScoringWidgetsFailure, (state, { error }) => ({
    ...state,
    teamScoringWidgets: { ...state.teamScoringWidgets, error },
  })),
  on(PresenterWidgetsActions.selectTeamScoringWidget, (state, { uuid }) => ({
    ...state,
    teamScoringWidgets: { ...state.teamScoringWidgets, selectedId: uuid },
  })),
  on(PresenterWidgetsActions.updateSelectedTeamScoringWidgetSuccess, (state, { update }) => ({
    ...state,
    teamScoringWidgets: teamScoringWidgetsAdapter.updateOne(update, state.teamScoringWidgets),
  })),
  on(PresenterWidgetsActions.updateSelectedTeamScoringWidgetFailure, (state, { error }) => ({
    ...state,
    teamScoringWidgets: { ...state.teamScoringWidgets, error },
  })),
  on(PresenterWidgetsActions.clearSelectedTeamScoringWidget, (state) => ({
    ...state,
    teamScoringWidgets: { ...state.teamScoringWidgets, selectedId: undefined },
  })),
  on(PresenterWidgetsActions.createTeamScoringWidget, (state) => ({
    ...state,
    teamScoringWidgets: { ...state.teamScoringWidgets, createLoaded: false },
  })),
  on(PresenterWidgetsActions.createTeamScoringWidgetSuccess, (state, { widget }) => ({
    ...state,
    teamScoringWidgets: teamScoringWidgetsAdapter.addOne(widget, { ...state.teamScoringWidgets, createLoaded: true }),
  })),
  on(PresenterWidgetsActions.createTeamScoringWidgetFailure, (state, { error }) => ({
    ...state,
    teamScoringWidgets: { ...state.teamScoringWidgets, error, createLoaded: true },
  })),
  on(PresenterWidgetsActions.deleteTeamScoringWidget, (state, { uuid }) => {
    const deleted = state.teamScoringWidgets.entities[uuid]
    const nextState = teamScoringWidgetsAdapter.removeOne(uuid, state.teamScoringWidgets)
    return {
      ...state,
      teamScoringWidgets: {
        ...nextState,
        optimisticDeleted: deleted
          ? { ...state.teamScoringWidgets.optimisticDeleted, [uuid]: deleted }
          : state.teamScoringWidgets.optimisticDeleted,
      },
    }
  }),
  on(PresenterWidgetsActions.deleteTeamScoringWidgetSuccess, (state, { uuid }) => ({
    ...state,
    teamScoringWidgets: {
      ...state.teamScoringWidgets,
      optimisticDeleted: Object.fromEntries(
        Object.entries(state.teamScoringWidgets.optimisticDeleted).filter(([key]) => key !== uuid),
      ),
    },
  })),
  on(PresenterWidgetsActions.deleteTeamScoringWidgetFailure, (state, { error }) => ({
    ...state,
    teamScoringWidgets: {
      ...state.teamScoringWidgets,
      error,
      optimisticDeleted: {},
      ...(Object.keys(state.teamScoringWidgets.optimisticDeleted).length
        ? teamScoringWidgetsAdapter.addMany(
            Object.values(state.teamScoringWidgets.optimisticDeleted),
            state.teamScoringWidgets,
          )
        : {}),
    },
  })),

  // Power Pledge Widget actions
  on(PresenterWidgetsActions.loadPowerPledgeWidgets, (state) => ({
    ...state,
    powerPledgeWidgets: { ...state.powerPledgeWidgets, loaded: false, error: null },
  })),
  on(PresenterWidgetsActions.loadPowerPledgeWidgetsSuccess, (state, { widgets }) => ({
    ...state,
    powerPledgeWidgets: powerPledgeWidgetsAdapter.setAll(widgets, {
      ...state.powerPledgeWidgets,
      loaded: true,
    }),
  })),
  on(PresenterWidgetsActions.loadPowerPledgeWidgetsFailure, (state, { error }) => ({
    ...state,
    powerPledgeWidgets: { ...state.powerPledgeWidgets, error },
  })),
  on(PresenterWidgetsActions.selectPowerPledgeWidget, (state, { uuid }) => ({
    ...state,
    powerPledgeWidgets: { ...state.powerPledgeWidgets, selectedId: uuid },
  })),
  on(PresenterWidgetsActions.updateSelectedPowerPledgeWidgetSuccess, (state, { update }) => ({
    ...state,
    powerPledgeWidgets: powerPledgeWidgetsAdapter.updateOne(update, state.powerPledgeWidgets),
  })),
  on(PresenterWidgetsActions.updateSelectedPowerPledgeWidgetFailure, (state, { error }) => ({
    ...state,
    powerPledgeWidgets: { ...state.powerPledgeWidgets, error },
  })),
  on(PresenterWidgetsActions.clearSelectedPowerPledgeWidget, (state) => ({
    ...state,
    powerPledgeWidgets: { ...state.powerPledgeWidgets, selectedId: undefined },
  })),
  on(PresenterWidgetsActions.createPowerPledgeWidget, (state) => ({
    ...state,
    powerPledgeWidgets: { ...state.powerPledgeWidgets, createLoaded: false },
  })),
  on(PresenterWidgetsActions.createPowerPledgeWidgetSuccess, (state, { widget }) => ({
    ...state,
    powerPledgeWidgets: powerPledgeWidgetsAdapter.addOne(widget, { ...state.powerPledgeWidgets, createLoaded: true }),
  })),
  on(PresenterWidgetsActions.createPowerPledgeWidgetFailure, (state, { error }) => ({
    ...state,
    powerPledgeWidgets: { ...state.powerPledgeWidgets, error, createLoaded: true },
  })),
  on(PresenterWidgetsActions.deletePowerPledgeWidget, (state, { uuid }) => {
    const deleted = state.powerPledgeWidgets.entities[uuid]
    const nextState = powerPledgeWidgetsAdapter.removeOne(uuid, state.powerPledgeWidgets)
    return {
      ...state,
      powerPledgeWidgets: {
        ...nextState,
        optimisticDeleted: deleted
          ? { ...state.powerPledgeWidgets.optimisticDeleted, [uuid]: deleted }
          : state.powerPledgeWidgets.optimisticDeleted,
      },
    }
  }),
  on(PresenterWidgetsActions.deletePowerPledgeWidgetSuccess, (state, { uuid }) => ({
    ...state,
    powerPledgeWidgets: {
      ...state.powerPledgeWidgets,
      optimisticDeleted: Object.fromEntries(
        Object.entries(state.powerPledgeWidgets.optimisticDeleted).filter(([key]) => key !== uuid),
      ),
    },
  })),
  on(PresenterWidgetsActions.deletePowerPledgeWidgetFailure, (state, { error }) => ({
    ...state,
    powerPledgeWidgets: {
      ...state.powerPledgeWidgets,
      error,
      optimisticDeleted: {},
      ...(Object.keys(state.powerPledgeWidgets.optimisticDeleted).length
        ? powerPledgeWidgetsAdapter.addMany(
            Object.values(state.powerPledgeWidgets.optimisticDeleted),
            state.powerPledgeWidgets,
          )
        : {}),
    },
  })),

  // Notification Widget actions
  on(PresenterWidgetsActions.loadNotificationWidgets, (state) => ({
    ...state,
    notificationWidgets: { ...state.notificationWidgets, loaded: false, error: null },
  })),
  on(PresenterWidgetsActions.loadNotificationWidgetsSuccess, (state, { widgets }) => ({
    ...state,
    notificationWidgets: notificationWidgetsAdapter.setAll(widgets, {
      ...state.notificationWidgets,
      loaded: true,
    }),
  })),
  on(PresenterWidgetsActions.loadNotificationWidgetsFailure, (state, { error }) => ({
    ...state,
    notificationWidgets: { ...state.notificationWidgets, error },
  })),
  on(PresenterWidgetsActions.selectNotificationWidget, (state, { uuid }) => ({
    ...state,
    notificationWidgets: { ...state.notificationWidgets, selectedId: uuid },
  })),
  on(PresenterWidgetsActions.updateSelectedNotificationWidgetSuccess, (state, { update }) => ({
    ...state,
    notificationWidgets: notificationWidgetsAdapter.updateOne(update, state.notificationWidgets),
  })),
  on(PresenterWidgetsActions.updateSelectedNotificationWidgetFailure, (state, { error }) => ({
    ...state,
    notificationWidgets: { ...state.notificationWidgets, error },
  })),
  on(PresenterWidgetsActions.clearSelectedNotificationWidget, (state) => ({
    ...state,
    notificationWidgets: { ...state.notificationWidgets, selectedId: undefined },
  })),
  on(PresenterWidgetsActions.createNotificationWidget, (state) => ({
    ...state,
    notificationWidgets: { ...state.notificationWidgets, createLoaded: false },
  })),
  on(PresenterWidgetsActions.createNotificationWidgetSuccess, (state, { widget }) => ({
    ...state,
    notificationWidgets: notificationWidgetsAdapter.addOne(widget, {
      ...state.notificationWidgets,
      createLoaded: true,
    }),
  })),
  on(PresenterWidgetsActions.createNotificationWidgetFailure, (state, { error }) => ({
    ...state,
    notificationWidgets: { ...state.notificationWidgets, error, createLoaded: true },
  })),
  on(PresenterWidgetsActions.deleteNotificationWidget, (state, { uuid }) => {
    const deleted = state.notificationWidgets.entities[uuid]
    const nextState = notificationWidgetsAdapter.removeOne(uuid, state.notificationWidgets)
    return {
      ...state,
      notificationWidgets: {
        ...nextState,
        optimisticDeleted: deleted
          ? { ...state.notificationWidgets.optimisticDeleted, [uuid]: deleted }
          : state.notificationWidgets.optimisticDeleted,
      },
    }
  }),
  on(PresenterWidgetsActions.deleteNotificationWidgetSuccess, (state, { uuid }) => ({
    ...state,
    notificationWidgets: {
      ...state.notificationWidgets,
      optimisticDeleted: Object.fromEntries(
        Object.entries(state.notificationWidgets.optimisticDeleted).filter(([key]) => key !== uuid),
      ),
    },
  })),
  on(PresenterWidgetsActions.deleteNotificationWidgetFailure, (state, { error }) => ({
    ...state,
    notificationWidgets: {
      ...state.notificationWidgets,
      error,
      optimisticDeleted: {},
      ...(Object.keys(state.notificationWidgets.optimisticDeleted).length
        ? notificationWidgetsAdapter.addMany(
            Object.values(state.notificationWidgets.optimisticDeleted),
            state.notificationWidgets,
          )
        : {}),
    },
  })),

  // Item Table Widget actions
  on(PresenterWidgetsActions.loadItemTableWidgets, (state) => ({
    ...state,
    itemTableWidgets: { ...state.itemTableWidgets, loaded: false, error: null },
  })),
  on(PresenterWidgetsActions.loadItemTableWidgetsSuccess, (state, { widgets }) => ({
    ...state,
    itemTableWidgets: itemTableWidgetsAdapter.setAll(widgets, { ...state.itemTableWidgets, loaded: true }),
  })),
  on(PresenterWidgetsActions.loadItemTableWidgetsFailure, (state, { error }) => ({
    ...state,
    itemTableWidgets: { ...state.itemTableWidgets, error },
  })),
  on(PresenterWidgetsActions.selectItemTableWidget, (state, { uuid }) => ({
    ...state,
    itemTableWidgets: { ...state.itemTableWidgets, selectedId: uuid },
  })),
  on(PresenterWidgetsActions.updateSelectedItemTableWidgetSuccess, (state, { update }) => ({
    ...state,
    itemTableWidgets: itemTableWidgetsAdapter.updateOne(update, state.itemTableWidgets),
  })),
  on(PresenterWidgetsActions.updateSelectedItemTableWidgetFailure, (state, { error }) => ({
    ...state,
    itemTableWidgets: { ...state.itemTableWidgets, error },
  })),
  on(PresenterWidgetsActions.clearSelectedItemTableWidget, (state) => ({
    ...state,
    itemTableWidgets: { ...state.itemTableWidgets, selectedId: undefined },
  })),
  on(PresenterWidgetsActions.createItemTableWidget, (state) => ({
    ...state,
    itemTableWidgets: { ...state.itemTableWidgets, createLoaded: false },
  })),
  on(PresenterWidgetsActions.createItemTableWidgetSuccess, (state, { widget }) => ({
    ...state,
    itemTableWidgets: itemTableWidgetsAdapter.addOne(widget, { ...state.itemTableWidgets, createLoaded: true }),
  })),
  on(PresenterWidgetsActions.createItemTableWidgetFailure, (state, { error }) => ({
    ...state,
    itemTableWidgets: { ...state.itemTableWidgets, error, createLoaded: true },
  })),
  on(PresenterWidgetsActions.deleteItemTableWidget, (state, { uuid }) => {
    const deleted = state.itemTableWidgets.entities[uuid]
    const nextState = itemTableWidgetsAdapter.removeOne(uuid, state.itemTableWidgets)
    return {
      ...state,
      itemTableWidgets: {
        ...nextState,
        optimisticDeleted: deleted
          ? { ...state.itemTableWidgets.optimisticDeleted, [uuid]: deleted }
          : state.itemTableWidgets.optimisticDeleted,
      },
    }
  }),
  on(PresenterWidgetsActions.deleteItemTableWidgetSuccess, (state, { uuid }) => ({
    ...state,
    itemTableWidgets: {
      ...state.itemTableWidgets,
      optimisticDeleted: Object.fromEntries(
        Object.entries(state.itemTableWidgets.optimisticDeleted).filter(([key]) => key !== uuid),
      ),
    },
  })),
  on(PresenterWidgetsActions.deleteItemTableWidgetFailure, (state, { error }) => ({
    ...state,
    itemTableWidgets: {
      ...state.itemTableWidgets,
      error,
      optimisticDeleted: {},
      ...(Object.keys(state.itemTableWidgets.optimisticDeleted).length
        ? itemTableWidgetsAdapter.addMany(
            Object.values(state.itemTableWidgets.optimisticDeleted),
            state.itemTableWidgets,
          )
        : {}),
    },
  })),

  // Raffle Winners Widget actions
  on(PresenterWidgetsActions.loadRaffleWinnersWidgets, (state) => ({
    ...state,
    raffleWinnersWidgets: { ...state.raffleWinnersWidgets, loaded: false, error: null },
  })),
  on(PresenterWidgetsActions.loadRaffleWinnersWidgetsSuccess, (state, { widgets }) => ({
    ...state,
    raffleWinnersWidgets: raffleWinnersWidgetsAdapter.setAll(widgets, {
      ...state.raffleWinnersWidgets,
      loaded: true,
    }),
  })),
  on(PresenterWidgetsActions.loadRaffleWinnersWidgetsFailure, (state, { error }) => ({
    ...state,
    raffleWinnersWidgets: { ...state.raffleWinnersWidgets, error },
  })),
  on(PresenterWidgetsActions.selectRaffleWinnersWidget, (state, { uuid }) => ({
    ...state,
    raffleWinnersWidgets: { ...state.raffleWinnersWidgets, selectedId: uuid },
  })),
  on(PresenterWidgetsActions.updateSelectedRaffleWinnersWidgetSuccess, (state, { update }) => ({
    ...state,
    raffleWinnersWidgets: raffleWinnersWidgetsAdapter.updateOne(update, state.raffleWinnersWidgets),
  })),
  on(PresenterWidgetsActions.updateSelectedRaffleWinnersWidgetFailure, (state, { error }) => ({
    ...state,
    raffleWinnersWidgets: { ...state.raffleWinnersWidgets, error },
  })),
  on(PresenterWidgetsActions.clearSelectedRaffleWinnersWidget, (state) => ({
    ...state,
    raffleWinnersWidgets: { ...state.raffleWinnersWidgets, selectedId: undefined },
  })),
  on(PresenterWidgetsActions.createRaffleWinnersWidget, (state) => ({
    ...state,
    raffleWinnersWidgets: { ...state.raffleWinnersWidgets, createLoaded: false },
  })),
  on(PresenterWidgetsActions.createRaffleWinnersWidgetSuccess, (state, { widget }) => ({
    ...state,
    raffleWinnersWidgets: raffleWinnersWidgetsAdapter.addOne(widget, {
      ...state.raffleWinnersWidgets,
      createLoaded: true,
    }),
  })),
  on(PresenterWidgetsActions.createRaffleWinnersWidgetFailure, (state, { error }) => ({
    ...state,
    raffleWinnersWidgets: { ...state.raffleWinnersWidgets, error, createLoaded: true },
  })),
  on(PresenterWidgetsActions.deleteRaffleWinnersWidget, (state, { uuid }) => {
    const deleted = state.raffleWinnersWidgets.entities[uuid]
    const nextState = raffleWinnersWidgetsAdapter.removeOne(uuid, state.raffleWinnersWidgets)
    return {
      ...state,
      raffleWinnersWidgets: {
        ...nextState,
        optimisticDeleted: deleted
          ? { ...state.raffleWinnersWidgets.optimisticDeleted, [uuid]: deleted }
          : state.raffleWinnersWidgets.optimisticDeleted,
      },
    }
  }),
  on(PresenterWidgetsActions.deleteRaffleWinnersWidgetSuccess, (state, { uuid }) => ({
    ...state,
    raffleWinnersWidgets: {
      ...state.raffleWinnersWidgets,
      optimisticDeleted: Object.fromEntries(
        Object.entries(state.raffleWinnersWidgets.optimisticDeleted).filter(([key]) => key !== uuid),
      ),
    },
  })),
  on(PresenterWidgetsActions.deleteRaffleWinnersWidgetFailure, (state, { error }) => ({
    ...state,
    raffleWinnersWidgets: {
      ...state.raffleWinnersWidgets,
      error,
      optimisticDeleted: {},
      ...(Object.keys(state.raffleWinnersWidgets.optimisticDeleted).length
        ? raffleWinnersWidgetsAdapter.addMany(
            Object.values(state.raffleWinnersWidgets.optimisticDeleted),
            state.raffleWinnersWidgets,
          )
        : {}),
    },
  })),

  // Honour Roll Widget actions
  on(PresenterWidgetsActions.loadHonourRollWidgets, (state) => ({
    ...state,
    honourRollWidgets: { ...state.honourRollWidgets, loaded: false, error: null },
  })),
  on(PresenterWidgetsActions.loadHonourRollWidgetsSuccess, (state, { widgets }) => ({
    ...state,
    honourRollWidgets: honourRollWidgetsAdapter.setAll(widgets, {
      ...state.honourRollWidgets,
      loaded: true,
    }),
  })),
  on(PresenterWidgetsActions.loadHonourRollWidgetsFailure, (state, { error }) => ({
    ...state,
    honourRollWidgets: { ...state.honourRollWidgets, error },
  })),
  on(PresenterWidgetsActions.selectHonourRollWidget, (state, { uuid }) => ({
    ...state,
    honourRollWidgets: { ...state.honourRollWidgets, selectedId: uuid },
  })),
  on(PresenterWidgetsActions.updateSelectedHonourRollWidgetSuccess, (state, { update }) => ({
    ...state,
    honourRollWidgets: honourRollWidgetsAdapter.updateOne(update, state.honourRollWidgets),
  })),
  on(PresenterWidgetsActions.updateSelectedHonourRollWidgetFailure, (state, { error }) => ({
    ...state,
    honourRollWidgets: { ...state.honourRollWidgets, error },
  })),
  on(PresenterWidgetsActions.clearSelectedHonourRollWidget, (state) => ({
    ...state,
    honourRollWidgets: { ...state.honourRollWidgets, selectedId: undefined },
  })),
  on(PresenterWidgetsActions.createHonourRollWidget, (state) => ({
    ...state,
    honourRollWidgets: { ...state.honourRollWidgets, createLoaded: false },
  })),
  on(PresenterWidgetsActions.createHonourRollWidgetSuccess, (state, { widget }) => ({
    ...state,
    honourRollWidgets: honourRollWidgetsAdapter.addOne(widget, { ...state.honourRollWidgets, createLoaded: true }),
  })),
  on(PresenterWidgetsActions.createHonourRollWidgetFailure, (state, { error }) => ({
    ...state,
    honourRollWidgets: { ...state.honourRollWidgets, error, createLoaded: true },
  })),
  on(PresenterWidgetsActions.deleteHonourRollWidget, (state, { uuid }) => {
    const deleted = state.honourRollWidgets.entities[uuid]
    const nextState = honourRollWidgetsAdapter.removeOne(uuid, state.honourRollWidgets)
    return {
      ...state,
      honourRollWidgets: {
        ...nextState,
        optimisticDeleted: deleted
          ? { ...state.honourRollWidgets.optimisticDeleted, [uuid]: deleted }
          : state.honourRollWidgets.optimisticDeleted,
      },
    }
  }),
  on(PresenterWidgetsActions.deleteHonourRollWidgetSuccess, (state, { uuid }) => ({
    ...state,
    honourRollWidgets: {
      ...state.honourRollWidgets,
      optimisticDeleted: Object.fromEntries(
        Object.entries(state.honourRollWidgets.optimisticDeleted).filter(([key]) => key !== uuid),
      ),
    },
  })),
  on(PresenterWidgetsActions.deleteHonourRollWidgetFailure, (state, { error }) => ({
    ...state,
    honourRollWidgets: {
      ...state.honourRollWidgets,
      error,
      optimisticDeleted: {},
      ...(Object.keys(state.honourRollWidgets.optimisticDeleted).length
        ? honourRollWidgetsAdapter.addMany(
            Object.values(state.honourRollWidgets.optimisticDeleted),
            state.honourRollWidgets,
          )
        : {}),
    },
  })),

  // Thermometer Widget actions
  on(PresenterWidgetsActions.loadThermometerWidgets, (state) => ({
    ...state,
    thermometerWidgets: { ...state.thermometerWidgets, loaded: false, error: null },
  })),
  on(PresenterWidgetsActions.loadThermometerWidgetsSuccess, (state, { widgets }) => ({
    ...state,
    thermometerWidgets: thermometerWidgetsAdapter.setAll(widgets, {
      ...state.thermometerWidgets,
      loaded: true,
    }),
  })),
  on(PresenterWidgetsActions.loadThermometerWidgetsFailure, (state, { error }) => ({
    ...state,
    thermometerWidgets: { ...state.thermometerWidgets, error },
  })),
  on(PresenterWidgetsActions.selectThermometerWidget, (state, { uuid }) => ({
    ...state,
    thermometerWidgets: { ...state.thermometerWidgets, selectedId: uuid },
  })),
  on(PresenterWidgetsActions.updateSelectedThermometerWidgetSuccess, (state, { update }) => ({
    ...state,
    thermometerWidgets: thermometerWidgetsAdapter.updateOne(update, state.thermometerWidgets),
  })),
  on(PresenterWidgetsActions.updateSelectedThermometerWidgetFailure, (state, { error }) => ({
    ...state,
    thermometerWidgets: { ...state.thermometerWidgets, error },
  })),
  on(PresenterWidgetsActions.clearSelectedThermometerWidget, (state) => ({
    ...state,
    thermometerWidgets: { ...state.thermometerWidgets, selectedId: undefined },
  })),
  on(PresenterWidgetsActions.createThermometerWidget, (state) => ({
    ...state,
    thermometerWidgets: { ...state.thermometerWidgets, createLoaded: false },
  })),
  on(PresenterWidgetsActions.createThermometerWidgetSuccess, (state, { widget }) => ({
    ...state,
    thermometerWidgets: thermometerWidgetsAdapter.addOne(widget, { ...state.thermometerWidgets, createLoaded: true }),
  })),
  on(PresenterWidgetsActions.createThermometerWidgetFailure, (state, { error }) => ({
    ...state,
    thermometerWidgets: { ...state.thermometerWidgets, error, createLoaded: true },
  })),
  on(PresenterWidgetsActions.deleteThermometerWidget, (state, { uuid }) => {
    const deleted = state.thermometerWidgets.entities[uuid]
    const nextState = thermometerWidgetsAdapter.removeOne(uuid, state.thermometerWidgets)
    return {
      ...state,
      thermometerWidgets: {
        ...nextState,
        optimisticDeleted: deleted
          ? { ...state.thermometerWidgets.optimisticDeleted, [uuid]: deleted }
          : state.thermometerWidgets.optimisticDeleted,
      },
    }
  }),
  on(PresenterWidgetsActions.deleteThermometerWidgetSuccess, (state, { uuid }) => ({
    ...state,
    thermometerWidgets: {
      ...state.thermometerWidgets,
      optimisticDeleted: Object.fromEntries(
        Object.entries(state.thermometerWidgets.optimisticDeleted).filter(([key]) => key !== uuid),
      ),
    },
  })),
  on(PresenterWidgetsActions.deleteThermometerWidgetFailure, (state, { error }) => ({
    ...state,
    thermometerWidgets: {
      ...state.thermometerWidgets,
      error,
      optimisticDeleted: {},
      ...(Object.keys(state.thermometerWidgets.optimisticDeleted).length
        ? thermometerWidgetsAdapter.addMany(
            Object.values(state.thermometerWidgets.optimisticDeleted),
            state.thermometerWidgets,
          )
        : {}),
    },
  })),

  on(PresenterWidgetsActions.updateThermometerWidgetForRealtime, (state, { update }) => ({
    ...state,
    thermometerWidgets: thermometerWidgetsAdapter.upsertOne(
      {
        ...(state.thermometerWidgets.entities[update.id] ?? ({} as ThermometerWidgetModel)),
        ...(update.changes as Partial<ThermometerWidgetModel>),
        uuid: update.id as string,
      } as ThermometerWidgetModel,
      state.thermometerWidgets,
    ),
    selectedWidget:
      state.selectedWidgetType === WidgetType.THERMOMETER && state.selectedWidget?.uuid === update.id
        ? ({ ...state.selectedWidget, ...(update.changes as Partial<ThermometerWidgetModel>) } as WidgetModel)
        : state.selectedWidget,
  })),

  on(PresenterWidgetsActions.updateTotalRaisedWidgetForRealtime, (state, { update }) => ({
    ...state,
    totalRaisedWidgets: thermometerWidgetsAdapter.upsertOne(
      {
        ...(state.totalRaisedWidgets.entities[update.id] ?? ({} as ThermometerWidgetModel)),
        ...(update.changes as Partial<TotalRaisedWidgetModel>),
        uuid: update.id as string,
      } as TotalRaisedWidgetModel,
      state.totalRaisedWidgets,
    ),
  })),

  on(PresenterWidgetsActions.updatePowerPledgeWidgetForRealtime, (state, { update }) => ({
    ...state,
    powerPledgeWidgets: powerPledgeWidgetsAdapter.upsertOne(
      {
        ...(state.powerPledgeWidgets.entities[update.id] ?? ({} as PowerPledgeWidgetModel)),
        ...(update.changes as Partial<PowerPledgeWidgetModel>),
        uuid: update.id as string,
      } as PowerPledgeWidgetModel,
      state.powerPledgeWidgets,
    ),
    selectedWidget:
      state.selectedWidgetType === WidgetType.POWER_PLEDGE && state.selectedWidget?.uuid === update.id
        ? ({ ...state.selectedWidget, ...(update.changes as Partial<PowerPledgeWidgetModel>) } as WidgetModel)
        : state.selectedWidget,
  })),

  // Raffle Draw Widget actions
  on(PresenterWidgetsActions.loadRaffleDrawWidgets, (state) => ({
    ...state,
    raffleDrawWidgets: { ...state.raffleDrawWidgets, loaded: false, error: null },
  })),
  on(PresenterWidgetsActions.loadRaffleDrawWidgetsSuccess, (state, { widgets }) => ({
    ...state,
    raffleDrawWidgets: raffleDrawWidgetsAdapter.setAll(widgets, {
      ...state.raffleDrawWidgets,
      loaded: true,
    }),
  })),
  on(PresenterWidgetsActions.loadRaffleDrawWidgetsFailure, (state, { error }) => ({
    ...state,
    raffleDrawWidgets: { ...state.raffleDrawWidgets, error },
  })),
  on(PresenterWidgetsActions.selectRaffleDrawWidget, (state, { uuid }) => ({
    ...state,
    raffleDrawWidgets: { ...state.raffleDrawWidgets, selectedId: uuid },
  })),
  on(PresenterWidgetsActions.updateSelectedRaffleDrawWidgetSuccess, (state, { update }) => ({
    ...state,
    raffleDrawWidgets: raffleDrawWidgetsAdapter.updateOne(update, state.raffleDrawWidgets),
  })),
  on(PresenterWidgetsActions.updateSelectedRaffleDrawWidgetFailure, (state, { error }) => ({
    ...state,
    raffleDrawWidgets: { ...state.raffleDrawWidgets, error },
  })),
  on(PresenterWidgetsActions.clearSelectedRaffleDrawWidget, (state) => ({
    ...state,
    raffleDrawWidgets: { ...state.raffleDrawWidgets, selectedId: undefined },
  })),
  on(PresenterWidgetsActions.createRaffleDrawWidget, (state) => ({
    ...state,
    raffleDrawWidgets: { ...state.raffleDrawWidgets, createLoaded: false },
  })),
  on(PresenterWidgetsActions.createRaffleDrawWidgetSuccess, (state, { widget }) => ({
    ...state,
    raffleDrawWidgets: raffleDrawWidgetsAdapter.addOne(widget, { ...state.raffleDrawWidgets, createLoaded: true }),
  })),
  on(PresenterWidgetsActions.createRaffleDrawWidgetFailure, (state, { error }) => ({
    ...state,
    raffleDrawWidgets: { ...state.raffleDrawWidgets, error, createLoaded: true },
  })),
  on(PresenterWidgetsActions.deleteRaffleDrawWidget, (state, { uuid }) => {
    const deleted = state.raffleDrawWidgets.entities[uuid]
    const nextState = raffleDrawWidgetsAdapter.removeOne(uuid, state.raffleDrawWidgets)
    return {
      ...state,
      raffleDrawWidgets: {
        ...nextState,
        optimisticDeleted: deleted
          ? { ...state.raffleDrawWidgets.optimisticDeleted, [uuid]: deleted }
          : state.raffleDrawWidgets.optimisticDeleted,
      },
    }
  }),
  on(PresenterWidgetsActions.deleteRaffleDrawWidgetSuccess, (state, { uuid }) => ({
    ...state,
    raffleDrawWidgets: {
      ...state.raffleDrawWidgets,
      optimisticDeleted: Object.fromEntries(
        Object.entries(state.raffleDrawWidgets.optimisticDeleted).filter(([key]) => key !== uuid),
      ),
    },
  })),
  on(PresenterWidgetsActions.deleteRaffleDrawWidgetFailure, (state, { error }) => ({
    ...state,
    raffleDrawWidgets: {
      ...state.raffleDrawWidgets,
      error,
      optimisticDeleted: {},
      ...(Object.keys(state.raffleDrawWidgets.optimisticDeleted).length
        ? raffleDrawWidgetsAdapter.addMany(
            Object.values(state.raffleDrawWidgets.optimisticDeleted),
            state.raffleDrawWidgets,
          )
        : {}),
    },
  })),

  // QR Code Widget actions
  on(PresenterWidgetsActions.loadQrCodeWidgets, (state) => ({
    ...state,
    qrCodeWidgets: { ...state.qrCodeWidgets, loaded: false, error: null },
  })),
  on(PresenterWidgetsActions.loadQrCodeWidgetsSuccess, (state, { widgets }) => ({
    ...state,
    qrCodeWidgets: qrCodeWidgetsAdapter.setAll(widgets, {
      ...state.qrCodeWidgets,
      loaded: true,
    }),
  })),
  on(PresenterWidgetsActions.loadQrCodeWidgetsFailure, (state, { error }) => ({
    ...state,
    qrCodeWidgets: { ...state.qrCodeWidgets, error },
  })),
  on(PresenterWidgetsActions.selectQrCodeWidget, (state, { uuid }) => ({
    ...state,
    qrCodeWidgets: { ...state.qrCodeWidgets, selectedId: uuid },
  })),
  on(PresenterWidgetsActions.updateSelectedQrCodeWidgetSuccess, (state, { update }) => ({
    ...state,
    qrCodeWidgets: qrCodeWidgetsAdapter.updateOne(update, state.qrCodeWidgets),
  })),
  on(PresenterWidgetsActions.updateSelectedQrCodeWidgetFailure, (state, { error }) => ({
    ...state,
    qrCodeWidgets: { ...state.qrCodeWidgets, error },
  })),
  on(PresenterWidgetsActions.clearSelectedQrCodeWidget, (state) => ({
    ...state,
    qrCodeWidgets: { ...state.qrCodeWidgets, selectedId: undefined },
  })),
  on(PresenterWidgetsActions.createQrCodeWidget, (state) => ({
    ...state,
    qrCodeWidgets: { ...state.qrCodeWidgets, createLoaded: false },
  })),
  on(PresenterWidgetsActions.createQrCodeWidgetSuccess, (state, { widget }) => ({
    ...state,
    qrCodeWidgets: qrCodeWidgetsAdapter.addOne(widget, { ...state.qrCodeWidgets, createLoaded: true }),
  })),
  on(PresenterWidgetsActions.createQrCodeWidgetFailure, (state, { error }) => ({
    ...state,
    qrCodeWidgets: { ...state.qrCodeWidgets, error, createLoaded: true },
  })),
  on(PresenterWidgetsActions.deleteQrCodeWidget, (state, { uuid }) => {
    const deleted = state.qrCodeWidgets.entities[uuid]
    const nextState = qrCodeWidgetsAdapter.removeOne(uuid, state.qrCodeWidgets)
    return {
      ...state,
      qrCodeWidgets: {
        ...nextState,
        optimisticDeleted: deleted
          ? { ...state.qrCodeWidgets.optimisticDeleted, [uuid]: deleted }
          : state.qrCodeWidgets.optimisticDeleted,
      },
    }
  }),
  on(PresenterWidgetsActions.deleteQrCodeWidgetSuccess, (state, { uuid }) => ({
    ...state,
    qrCodeWidgets: {
      ...state.qrCodeWidgets,
      optimisticDeleted: Object.fromEntries(
        Object.entries(state.qrCodeWidgets.optimisticDeleted).filter(([key]) => key !== uuid),
      ),
    },
  })),
  on(PresenterWidgetsActions.deleteQrCodeWidgetFailure, (state, { error }) => ({
    ...state,
    qrCodeWidgets: {
      ...state.qrCodeWidgets,
      error,
      optimisticDeleted: {},
      ...(Object.keys(state.qrCodeWidgets.optimisticDeleted).length
        ? qrCodeWidgetsAdapter.addMany(Object.values(state.qrCodeWidgets.optimisticDeleted), state.qrCodeWidgets)
        : {}),
    },
  })),

  // Video Widget actions
  on(PresenterWidgetsActions.loadVideoWidgets, (state) => ({
    ...state,
    videoWidgets: { ...state.videoWidgets, loaded: false, error: null },
  })),
  on(PresenterWidgetsActions.loadVideoWidgetsSuccess, (state, { widgets }) => ({
    ...state,
    videoWidgets: videoWidgetsAdapter.setAll(widgets, { ...state.videoWidgets, loaded: true }),
  })),
  on(PresenterWidgetsActions.loadVideoWidgetsFailure, (state, { error }) => ({
    ...state,
    videoWidgets: { ...state.videoWidgets, error },
  })),
  on(PresenterWidgetsActions.selectVideoWidget, (state, { uuid }) => ({
    ...state,
    videoWidgets: { ...state.videoWidgets, selectedId: uuid },
  })),
  on(PresenterWidgetsActions.updateSelectedVideoWidgetSuccess, (state, { update }) => ({
    ...state,
    videoWidgets: videoWidgetsAdapter.updateOne(update, state.videoWidgets),
  })),
  on(PresenterWidgetsActions.updateSelectedVideoWidgetFailure, (state, { error }) => ({
    ...state,
    videoWidgets: { ...state.videoWidgets, error },
  })),
  on(PresenterWidgetsActions.clearSelectedVideoWidget, (state) => ({
    ...state,
    videoWidgets: { ...state.videoWidgets, selectedId: undefined },
  })),
  on(PresenterWidgetsActions.createVideoWidget, (state) => ({
    ...state,
    videoWidgets: { ...state.videoWidgets, createLoaded: false },
  })),
  on(PresenterWidgetsActions.createVideoWidgetSuccess, (state, { widget }) => ({
    ...state,
    videoWidgets: videoWidgetsAdapter.addOne(widget, { ...state.videoWidgets, createLoaded: true }),
  })),
  on(PresenterWidgetsActions.createVideoWidgetFailure, (state, { error }) => ({
    ...state,
    videoWidgets: { ...state.videoWidgets, error, createLoaded: true },
  })),
  on(PresenterWidgetsActions.deleteVideoWidget, (state, { uuid }) => {
    const deleted = state.videoWidgets.entities[uuid]
    const nextState = videoWidgetsAdapter.removeOne(uuid, state.videoWidgets)
    return {
      ...state,
      videoWidgets: {
        ...nextState,
        optimisticDeleted: deleted
          ? { ...state.videoWidgets.optimisticDeleted, [uuid]: deleted }
          : state.videoWidgets.optimisticDeleted,
      },
    }
  }),
  on(PresenterWidgetsActions.deleteVideoWidgetSuccess, (state, { uuid }) => ({
    ...state,
    videoWidgets: {
      ...state.videoWidgets,
      optimisticDeleted: Object.fromEntries(
        Object.entries(state.videoWidgets.optimisticDeleted).filter(([key]) => key !== uuid),
      ),
    },
  })),
  on(PresenterWidgetsActions.deleteVideoWidgetFailure, (state, { error }) => ({
    ...state,
    videoWidgets: {
      ...state.videoWidgets,
      error,
      optimisticDeleted: {},
      ...(Object.keys(state.videoWidgets.optimisticDeleted).length
        ? videoWidgetsAdapter.addMany(Object.values(state.videoWidgets.optimisticDeleted), state.videoWidgets)
        : {}),
    },
  })),
)

export function presenterWidgetsReducer(state: PresenterWidgetsState | undefined, action: Action) {
  return reducer(state, action)
}
